import { faBookBible } from '@fortawesome/free-solid-svg-icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { apiErrorDetail } from '@/admin/errorDetail';
import { LANGUAGE_LABELS } from '@/admin/languages';
import { problemKey } from '@/admin/publishing/blockProblemKey';
import {
  type Block,
  blocksProblem,
  toPayload,
} from '@/admin/publishing/blocks';
import { ChapterBlocksEditor } from '@/admin/publishing/ChapterBlocksEditor';
import {
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelLabel,
  SaveGroup,
} from '@/admin/ui/Editor';
import {
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  TextInput,
} from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { useUnsavedChanges } from '@/admin/ui/useUnsavedChanges';
import {
  EditConflictNotice,
  editConflict,
  formatWhen,
} from '@/admin/ui/Versions';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';

type Chapter = components['schemas']['AdminBibleChapterDetail'];

export const Route = createFileRoute('/admin/bible/$chapterId')({
  staticData: { minTier: 'ADMIN' },
  component: ChapterPage,
});

function chapterKey(id: number) {
  return api.adminGetBibleChapter.getQueryKey({ path: { chapter_id: id } });
}

function invalidateChapterLists() {
  api.adminListBibleChapters.invalidateQueries();
  // The language strip's "last edited" moves with every save.
  queryClient.invalidateQueries({
    queryKey: api.adminListBibleLanguages.getQueryKey({}),
  });
}

function ChapterPage() {
  const { t } = useTranslation();
  const { chapterId } = Route.useParams();
  const [epoch, setEpoch] = useState(0);
  const query = api.adminGetBibleChapter.useQuery({
    path: { chapter_id: Number(chapterId) },
  });
  if (query.isLoading)
    return <Message>{t('admin.publishing.chapter.loading')}</Message>;
  if (query.error || !query.data)
    return (
      <Message $error>{t('admin.publishing.chapter.errorLoading')}</Message>
    );
  // Keyed on the id, not the revision: a background refetch carrying another
  // admin's save must not drop unsaved edits. The editor bumps `epoch` after its
  // own save (or a reload after a conflict) to re-seed from the server's copy.
  return (
    <ChapterEditor
      key={`${query.data.id}:${epoch}`}
      chapter={query.data}
      onReseed={() => setEpoch((n) => n + 1)}
    />
  );
}

function ChapterEditor({
  chapter,
  onReseed,
}: {
  chapter: Chapter;
  onReseed: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(chapter.title);
  const [blocks, setBlocks] = useState<Block[]>(chapter.blocks);
  const [showProblem, setShowProblem] = useState(false);

  const formValues = JSON.stringify([title, blocks]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const { allowLeave, confirmDiscard, dialog } = useUnsavedChanges(dirty);

  // Pinned at mount: a background refetch must not advance it past a conflict.
  const [baseRevision] = useState(chapter.revision);
  const trimmed = title.trim();
  const problem = blocksProblem(blocks);

  const update = api.adminUpdateBibleChapter.useMutation(undefined, {
    onSuccess: (saved) => {
      queryClient.setQueryData(chapterKey(chapter.id), saved);
      invalidateChapterLists();
      onReseed();
    },
  });

  const save = () => {
    if (!trimmed || update.isPending) return;
    if (problem) {
      setShowProblem(true);
      return;
    }
    setShowProblem(false);
    update.mutate({
      path: { chapter_id: chapter.id },
      body: {
        title: trimmed,
        blocks: toPayload(blocks),
        expected_revision: baseRevision,
      },
    });
  };

  const back = () =>
    navigate({
      to: '/admin/bible/',
      search: { language: chapter.language, book: chapter.book_id },
    });

  return (
    <DetailPage>
      <div>
        <Link
          to="/admin/bible/"
          search={{ language: chapter.language, book: chapter.book_id }}
        >
          {t('admin.publishing.chapter.back')}
        </Link>
        <PageTitle>
          {chapter.book_name} {chapter.chapter_number}
        </PageTitle>
        <FieldHint>
          {t(LANGUAGE_LABELS[chapter.language])} ·{' '}
          {t('admin.publishing.chapter.revision', {
            revision: chapter.revision,
            when: formatWhen(chapter.updated_at),
            interpolation: { escapeValue: false },
          })}
          {chapter.updated_by ? ` · ${chapter.updated_by.name}` : ''}
        </FieldHint>
      </div>
      <EditorLayout>
        <EditorMain>
          <FormSection
            tone="content"
            icon={faBookBible}
            title={t('admin.common.details')}
          >
            <FormRow>
              <FormLabel htmlFor="chapter-title">
                {t('admin.common.title')}
              </FormLabel>
              <TextInput
                id="chapter-title"
                value={title}
                maxLength={300}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
              />
            </FormRow>
          </FormSection>
          <FormSection
            tone="feedback"
            icon={faBookBible}
            title={t('admin.publishing.chapter.blocks')}
            description={t('admin.publishing.chapter.blocksDescription')}
          >
            <ChapterBlocksEditor blocks={blocks} onChange={setBlocks} />
          </FormSection>
        </EditorMain>
        <EditorPanel aria-label={t('admin.publishing.chapter.panel')}>
          <SaveGroup
            heading={
              <PanelLabel>
                {t('admin.publishing.chapter.panelLabel')}
              </PanelLabel>
            }
            dirty={dirty}
            saving={update.isPending}
            saveDisabled={!trimmed}
            onSave={save}
            onCancel={() => confirmDiscard(back)}
          >
            {!trimmed && (
              <FieldHint>{t('admin.publishing.chapter.giveTitle')}</FieldHint>
            )}
            {showProblem && problem && (
              <ErrorText role="alert">
                {t(problemKey(problem), {
                  block: 'block' in problem ? problem.block : 0,
                  line: 'line' in problem ? problem.line : 0,
                  interpolation: { escapeValue: false },
                })}
              </ErrorText>
            )}
            {update.isError &&
              (editConflict(update.error) ? (
                <EditConflictNotice
                  error={update.error}
                  onReload={() => {
                    allowLeave();
                    invalidateChapterLists();
                    void queryClient
                      .refetchQueries({ queryKey: chapterKey(chapter.id) })
                      .then(onReseed);
                  }}
                />
              ) : (
                <ErrorText role="alert">
                  {apiErrorDetail(update.error) ??
                    t('admin.common.couldNotSave')}
                </ErrorText>
              ))}
          </SaveGroup>
        </EditorPanel>
      </EditorLayout>
      {dialog}
    </DetailPage>
  );
}

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
